import React, { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { Box, Tooltip } from '@mui/material';
import Header from "../header/Header";
import Sidebar from "../supervisor_home/sidebar/Sidebar";
import OtherSidebar from '../operator_home/sidebar/Sidebar';
import { useNavigate, useLocation } from "react-router-dom";
import './ReconcilationVerification.css';
import PdfToImageConverter from "./elemant/PdfURLToImageConverter";
import DisplayCSV from "./elemant/CSVDisplay";
import ImageBox from "./elemant/ImageBox";
import { ReconcilationTable } from "./ReconcilationTable";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { ReactSession } from "react-client-session";
import { untimeToast } from "../../toast";
import FormContext from "../../context/FormContext";
import { getFileExtensionFromUrl } from './elemant/utils';
import { toast } from "react-toastify";
import { qty } from "./elemant/Columns";
import RecoHeaderButton from "./elemant/RecoHeaderButton";
import DisplayExcel from "./elemant/ExcelDisplay";
import DocumentSelector from "./elemant/DocumentSelector";

/**
 * ReconcilationVerification Component
 * Main page for verifying and editing reconciliation data from uploaded documents
 */
function ReconcilationVerification() {
  // ============================================
  // STATE MANAGEMENT
  // ============================================
  
  // Data state
  const [reconcilationDetails, setReconcilationDetails] = useState([]);
  const [documents, setDocuments] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  
  // Document viewer state
  const [selectedDocument, setSelectedDocument] = useState(1);
  const [fileURL, setFileURL] = useState('');
  const [getExtension, setExtension] = useState('');
  const [imagesArray, setImagesArray] = useState([]);
  const [documentImagesMap, setDocumentImagesMap] = useState({});
  const [imageProperty, setImageProperty] = useState({ width: 600, angle: 0 });
  
  // UI state
  const [message, setMessage] = useState('');
  const [disabledState, setDisabledState] = useState(false);
  const [incorrectData, setIncorrectData] = useState([]);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [currentErrorIndex, setCurrentErrorIndex] = useState(-1);
  
  // Context & hooks
  const { setIsLoading, isLoading } = useContext(FormContext);
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Session data
  const box_id = ReactSession.get("box_id");
  const role = ReactSession.get("role");
  const isAdmin = role === "1" || role === 1;
  const readOnly = false; // Glenmark doesn't have admin read-only mode yet

  // ============================================
  // COMPUTED VALUES (Memoized)
  // ============================================
  
  /**
   * Filter data by selected document
   */
  const filteredData = useMemo(() => {
    if (!reconcilationDetails.length) return [];
    if (selectedDocument === 'all') return reconcilationDetails;
    return reconcilationDetails.filter(item => 
      (item.document_number || 1) === selectedDocument
    );
  }, [reconcilationDetails, selectedDocument]);

  /**
   * Get current document info
   */
  const currentDocInfo = useMemo(() => {
    return documents.find(d => d.document_number === selectedDocument) || documents[0] || {};
  }, [documents, selectedDocument]);

  /**
   * Calculate stats for display
   */
  const stats = useMemo(() => {
    const total = reconcilationDetails.length;
    const currentDocTotal = filteredData.length;
    const errorsCount = filteredData.filter(item => item.score < 100).length;
    const uniquePages = [...new Set(filteredData.map(item => item.page_no))].length;
    
    return { total, currentDocTotal, errorsCount, uniquePages };
  }, [reconcilationDetails, filteredData]);

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================
  
  /**
   * Extract unique documents from data
   */
  const extractDocuments = useCallback((data, fileUrlsFromResponse = []) => {
    const docMap = new Map();
    
    data.forEach(item => {
      const docNum = item.document_number || 1;
      if (!docMap.has(docNum)) {
        docMap.set(docNum, {
          document_number: docNum,
          file_name: item.file_name || `Document ${docNum}`,
          file_url: item.file_url || '',
          hasErrors: item.score < 100,
          recordCount: 0,
        });
      }
      docMap.get(docNum).recordCount++;
    });
    
    // Use file_urls from response if available
    if (fileUrlsFromResponse?.length > 0) {
      fileUrlsFromResponse.forEach((url, index) => {
        const docNum = index + 1;
        if (docMap.has(docNum)) {
          const doc = docMap.get(docNum);
          if (!doc.file_url) doc.file_url = url;
        } else {
          docMap.set(docNum, {
            document_number: docNum,
            file_name: `Document ${docNum}`,
            file_url: url,
            hasErrors: false,
            recordCount: 0,
          });
        }
      });
    }
    
    return Array.from(docMap.values()).sort((a, b) => a.document_number - b.document_number);
  }, []);

  /**
   * Fetch and convert PDF to images
   */
  const fetchPdfAndConvert = useCallback(async (url, docNumber = 1) => {
    if (documentImagesMap[docNumber]) {
      setImagesArray(documentImagesMap[docNumber]);
      return;
    }
    
    try {
      const imagesList = await PdfToImageConverter(url);
      if (imagesList) {
        setDocumentImagesMap(prev => ({ ...prev, [docNumber]: imagesList }));
        setImagesArray(imagesList);
      }
    } catch (error) {
      console.error('Error converting PDF:', error);
    }
  }, [documentImagesMap]);

  // ============================================
  // EFFECTS
  // ============================================
  
  /**
   * Prevent body scroll - application motto: no page scrolling
   */
  useEffect(() => {
    document.body.classList.add('reconcilation-page');
    return () => {
      document.body.classList.remove('reconcilation-page');
    };
  }, []);

  /**
   * Initialize data on mount
   */
  useEffect(() => {
    const initializeData = async () => {
      try {
        const { data } = location.state;
        const recoData = data.data;
        const fileUrls = data.file_urls || [data.file_url];
        const primaryUrl = fileUrls[0] || data.file_url;
        
        setMessage(data.error_message);
        
        // Assign sr_no per document
        const docCounters = {};
        const reconcilationData = recoData.map((row) => {
          const docNum = row.document_number || 1;
          if (!docCounters[docNum]) docCounters[docNum] = 1;
          
          return {
            ...row,
            sr_no: row.sr_no || docCounters[docNum]++,
            document_number: docNum,
            file_name: row.file_name || '',
            file_url: row.file_url || primaryUrl,
          };
        });

        const docs = extractDocuments(reconcilationData, fileUrls);
        setDocuments(docs);
        
        if (docs.length > 0) {
          setSelectedDocument(docs[0].document_number);
        }

        const firstDocData = reconcilationData.filter(row => 
          (row.document_number || 1) === (docs[0]?.document_number || 1)
        );
        const totalPages = [...new Set(firstDocData.map(row => row.page_no))].sort((a, b) => a - b);
        const currentCount = firstDocData.filter(row => row.page_no === 1).length;
        
        setLastPage(totalPages[totalPages.length - 1] || 1);
        setRowsPerPage(currentCount);
        setReconcilationDetails(reconcilationData);
        setFileURL(primaryUrl);
        setCurrentErrorIndex(-1); // Reset error index when data changes
        
        const extension = getFileExtensionFromUrl(primaryUrl);
        setExtension(extension);
        
        if (extension === 'pdf') {
          fetchPdfAndConvert(primaryUrl, docs[0]?.document_number || 1);
        }
      } catch (error) {
        console.error('Error initializing data:', error);
        toast.error('Error loading data');
      }
    };

    initializeData();
    
    return () => {
      toast.dismiss();
      setIsLoading(false);
    };
  }, []);

  /**
   * Update pagination when document/page changes
   */
  useEffect(() => {
    if (filteredData.length > 0) {
      const totalPages = [...new Set(filteredData.map(row => row.page_no))].sort((a, b) => a - b);
      setLastPage(totalPages[totalPages.length - 1] || 1);
      
      const currentCount = filteredData.filter(row => row.page_no === currentPage).length;
      setRowsPerPage(currentCount);
      
      if (!totalPages.includes(currentPage) && totalPages.length > 0) {
        setCurrentPage(totalPages[0]);
      }
    }
  }, [filteredData, currentPage]);

  /**
   * Update document viewer when document changes
   */
  useEffect(() => {
    const docInfo = documents.find(d => d.document_number === selectedDocument);
    
    if (docInfo?.file_url) {
      setFileURL(docInfo.file_url);
      const extension = getFileExtensionFromUrl(docInfo.file_url);
      setExtension(extension);
      
      if (extension === 'pdf') {
        if (documentImagesMap[selectedDocument]) {
          setImagesArray(documentImagesMap[selectedDocument]);
        } else {
          fetchPdfAndConvert(docInfo.file_url, selectedDocument);
        }
      }
    }
  }, [selectedDocument, documents, documentImagesMap, fetchPdfAndConvert]);

  // ============================================
  // HANDLERS
  // ============================================
  
  /**
   * Handle reconciliation after save
   */
  const handleReconcile = useCallback(async (id) => {
    try {
      const res = await axiosPrivate.get(`reconcilation/reconcile/${id}`);
      if (res.data.bool === false) {
        navigate('/upload');
      } else if (res.data.bool === true) {
        navigate('/reconcilation', { state: { data: res.data } });
      } else {
        navigate('/reconciled-report', { state: { data: res.data.data } });
      }
    } catch (err) {
      console.error('Reconcile error:', err);
      toast.error('Error during reconciliation');
    } finally {
      setIsLoading(false);
    }
  }, [axiosPrivate, navigate, setIsLoading]);

  /**
   * Download Excel/CSV file directly from URL
   */
  const downloadFileFromUrl = useCallback(async (url, fileName) => {
    try {
      // Fetch the file as a blob
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv, */*'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName || url.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback: open in new tab
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.warning('File opened in new tab. Please use browser download option.');
    }
  }, []);

  /**
   * Download current document as a single file (PDF / Excel / CSV)
   */
  const handleDownloadCurrentDocument = useCallback(async () => {
    if (!getExtension) {
      toast.error('File type not available');
      return;
    }
    const ext = getExtension.toLowerCase();
    const docName = currentDocInfo.file_name || `document-${selectedDocument}`;
    const fileName = docName.includes('.') ? docName : `${docName}.${ext}`;
    
    try {
      if (['xls', 'xlsx', 'xlsb', 'csv', 'pdf'].includes(ext)) {
        if (!fileURL) {
          toast.error('File URL not available');
          return;
        }
        await downloadFileFromUrl(fileURL, fileName);
        toast.success(`Downloaded ${fileName}`);
      }
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Error downloading document');
    }
  }, [currentDocInfo, selectedDocument, fileURL, getExtension, downloadFileFromUrl]);

  /**
   * Download all documents (all files)
   */
  const handleDownloadAllDocuments = useCallback(async () => {
    if (!documents || documents.length === 0) {
      toast.error('No documents available');
      return;
    }

    setIsDownloadingAll(true);
    setIsLoading(true);
    let totalPages = 0;
    let totalFiles = 0;

    try {
      for (const doc of documents) {
        const ext = (doc.file_url?.split('.').pop() || '').toLowerCase();
        const docName = doc.file_name || `document-${doc.document_number}`;
        
        // Handle Excel/CSV files - download directly
        if (['xls', 'xlsx', 'xlsb', 'csv'].includes(ext)) {
          if (doc.file_url) {
            await downloadFileFromUrl(doc.file_url, docName);
            totalFiles++;
            // Delay between files
            if (doc !== documents[documents.length - 1]) {
              await new Promise(resolve => setTimeout(resolve, 300));
            }
          }
          continue;
        }
        
        // Handle PDF files - download all pages
        if (ext === 'pdf') {
          // Get images for this document
          const docImages = documentImagesMap[doc.document_number] || [];
          
          if (docImages.length === 0) {
            // Try to fetch if not cached
            if (doc.file_url) {
              await fetchPdfAndConvert(doc.file_url, doc.document_number);
              // Wait a bit for conversion
              await new Promise(resolve => setTimeout(resolve, 500));
              const freshImages = documentImagesMap[doc.document_number] || [];
              if (freshImages.length > 0) {
                await downloadDocumentPages(freshImages, docName);
                totalPages += freshImages.length;
              }
            }
          } else {
            await downloadDocumentPages(docImages, docName);
            totalPages += docImages.length;
          }
        }
        
        // Delay between documents
        if (doc !== documents[documents.length - 1]) {
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }
      
      const successMsg = totalPages > 0 
        ? `Downloaded ${totalPages} pages from ${documents.length} documents`
        : `Downloaded ${totalFiles} file(s)`;
      toast.success(successMsg);
    } catch (error) {
      console.error('Error downloading all documents:', error);
      toast.error('Error downloading documents');
    } finally {
      setIsDownloadingAll(false);
      setIsLoading(false);
    }
  }, [documents, documentImagesMap, fetchPdfAndConvert, downloadFileFromUrl, setIsLoading]);

  /**
   * Helper to download pages of a document
   */
  const downloadDocumentPages = async (images, docName) => {
    const cleanName = docName.replace(/\.[^/.]+$/, '');
    for (let i = 0; i < images.length; i++) {
      const link = document.createElement('a');
      link.href = images[i];
      link.download = `${cleanName}-page-${i + 1}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      if (i < images.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }
  };

  /**
   * Handle page change
   */
  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 1 && newPage <= lastPage) {
      setCurrentPage(newPage);
    }
  }, [lastPage]);

  /**
   * Get all errors from data
   */
  const getAllErrors = useCallback(() => {
    return reconcilationDetails.filter(item => item.score < 100);
  }, [reconcilationDetails]);

  /**
   * Navigate to next error (cycles through all errors)
   */
  const navigateToNextError = useCallback(() => {
    const allErrors = getAllErrors();
    
    if (allErrors.length === 0) {
      toast.info('No errors found');
      setCurrentErrorIndex(-1);
      return;
    }

    // Calculate next error index (cycle through)
    const nextIndex = (currentErrorIndex + 1) % allErrors.length;
    setCurrentErrorIndex(nextIndex);
    
    const error = allErrors[nextIndex];
    const errorDoc = error.document_number || 1;
    const errorPage = error.page_no || 1;

    // Navigate to the document containing the error
    if (errorDoc !== selectedDocument) {
      setSelectedDocument(errorDoc);
      // Wait for document to load, then navigate to page
      setTimeout(() => {
        setCurrentPage(errorPage);
        toast.success(`Error ${nextIndex + 1}/${allErrors.length} - Document ${errorDoc}, Page ${errorPage}`);
      }, 500);
    } else {
      // Same document, just change page
      setCurrentPage(errorPage);
      toast.success(`Error ${nextIndex + 1}/${allErrors.length} - Page ${errorPage}`);
    }
  }, [getAllErrors, currentErrorIndex, selectedDocument, setSelectedDocument]);

  /**
   * Submit reconciliation data
   */
  const handleSubmit = useCallback(() => {
    toast.dismiss();
    setIncorrectData([]);
    
    const updatedData = JSON.parse(JSON.stringify(reconcilationDetails));
    if (!updatedData[0]?.box_id) {
      updatedData.unshift({ box_id });
    }

    // Validate quantities
    const reg = /^\d+(\.\d{1,2})?$/;
    const invalidRows = updatedData.filter((row, i) => 
      i > 0 && (!reg.test(row.quantity) || qty.includes(row.quantity))
    );

    if (invalidRows.length > 0) {
      setIncorrectData(invalidRows);
      const pages = [...new Set(invalidRows.map(row => row.page_no))].join(', ');
      untimeToast(`Invalid quantities on page(s): ${pages}`);
      return;
    }

    setIsLoading(true);
    axiosPrivate
      .post('/reconcilation/save-reconciliation/', updatedData)
      .then(() => handleReconcile(box_id))
      .catch(err => {
        console.error('Save error:', err);
        toast.error('Error saving data');
      })
      .finally(() => setIsLoading(false));
  }, [reconcilationDetails, box_id, axiosPrivate, handleReconcile, setIsLoading]);

  // ============================================
  // RENDER HELPERS
  // ============================================
  
  /**
   * Render document preview based on file type
   */
  const renderDocumentPreview = () => {
    if (!getExtension) {
      return <div>Unable to determine file type</div>;
    }
    const currentImages = documentImagesMap[selectedDocument] || imagesArray;
    const ext = getExtension.toLowerCase();
    
    switch (ext) {
      case 'pdf':
        return (
          <ImageBox 
            key={`pdf-${selectedDocument}`}
            imagesArray={currentImages}
            currentPage={currentPage}
            imageProperty={imageProperty}
            setImageProperty={setImageProperty}
            fileName={currentDocInfo.file_name?.replace(/\.[^/.]+$/, '') || `document-${selectedDocument}`}
          />
        );
      case 'csv':
        return <DisplayCSV key={`csv-${selectedDocument}-${fileURL}`} url={fileURL} />;
      case 'xls':
      case 'xlsx':
      case 'xlsb':
        return <DisplayExcel key={`excel-${selectedDocument}-${fileURL}`} url={fileURL} />;
      default:
        return (
          <div className="document-preview-empty">
            <div className="document-preview-empty-icon">📄</div>
            <p>No preview available</p>
          </div>
        );
    }
  };

  /**
   * Render pagination controls
   */
  const renderPagination = () => (
    <div className="reco-pagination-wrapper">
      <div className="reco-pagination-controls">
        <button 
          className="reco-page-btn"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          title="Previous Page"
        >
          ◀
        </button>
        
        <div className="reco-page-info">
          <span>Page</span>
          <span className="reco-page-current">{currentPage}</span>
          <span>of {lastPage}</span>
        </div>
        
        <button 
          className="reco-page-btn"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage >= lastPage}
          title="Next Page"
        >
          ▶
        </button>
      </div>
      
      <div className="reco-pagination-stats">
        <span>📊 {rowsPerPage} items on page</span>
        <span>📁 {stats.currentDocTotal} in document</span>
        {stats.errorsCount > 0 && (
          <span 
            style={{ 
              color: 'var(--reco-error)',
              cursor: 'pointer',
              textDecoration: 'underline',
            }}
            onClick={navigateToNextError}
            title="Click to navigate to next error"
            className="reco-error-clickable-text"
          >
            ⚠️ {stats.errorsCount} errors
          </span>
        )}
      </div>
    </div>
  );

  /**
   * Render stats bar
   */
  const renderStatsBar = () => (
    <div className="reco-stats-bar">
      <div className="reco-stat-item">
        <span className="reco-stat-value">{documents.length}</span>
        <span className="reco-stat-label">Documents</span>
      </div>
      <div className="reco-stat-item">
        <span className="reco-stat-value">{stats.total}</span>
        <span className="reco-stat-label">Total Records</span>
      </div>
      <div className="reco-stat-item">
        <span className="reco-stat-value">{stats.uniquePages}</span>
        <span className="reco-stat-label">Pages</span>
      </div>
      {stats.errorsCount > 0 && (
        <div 
          className="reco-stat-item reco-error-clickable" 
          style={{ 
            background: '#fff5f5',
            cursor: 'pointer',
          }}
          onClick={navigateToNextError}
          title="Click to navigate to next error"
        >
          <span className="reco-stat-value" style={{ color: 'var(--reco-error)' }}>
            {stats.errorsCount}
          </span>
          <span className="reco-stat-label">Errors</span>
        </div>
      )}
    </div>
  );

  // ============================================
  // MAIN RENDER
  // ============================================
  
  return (
    <section className="reconcilation__verification__main">
      <Header />
      {role === '2' ? (
        <Sidebar className="sidebar-operator-home" />
      ) : (
        <OtherSidebar className="sidebar-operator-home" />
      )}
      
      <section className="reconcilation__verification__body">
        {/* Page Header */}
        <div className="reco-page-header">
          <h1 className="reconcilation__verification__tittle">
            Claim Order Verification
          </h1>
          {!readOnly && (
            <RecoHeaderButton 
              fileURL={fileURL}
              setIsLoading={setIsLoading}
              getExtension={getExtension}
              disabledState={disabledState}
            />
          )}
        </div>


        {/* Stats Bar */}
        {renderStatsBar()}

        {/* Error Message */}
        {message && (
          <div className="reco-error-message">
            <span>⚠️</span>
            <span>{message}</span>
          </div>
        )}

        {/* Document Selector */}
        {documents.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <DocumentSelector
                documents={documents}
                selectedDocument={selectedDocument}
                setSelectedDocument={setSelectedDocument}
                setCurrentPage={setCurrentPage}
              />
            </Box>
            {documents.length > 1 && (
              <Tooltip title={`Download All ${documents.length} Documents`} arrow>
                <button
                  onClick={handleDownloadAllDocuments}
                  disabled={disabledState || isDownloadingAll}
                  className="reco-download-all-btn"
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #1a73e8, #0d47a1)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap',
                    transition: 'all 0.2s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!disabledState && !isDownloadingAll) {
                      e.target.style.transform = 'scale(1.05)';
                      e.target.style.boxShadow = '0 4px 12px rgba(26, 115, 232, 0.4)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <span>📥</span>
                  <span>Download All</span>
                </button>
              </Tooltip>
            )}
          </Box>
        )}

        {/* Pagination */}
        {renderPagination()}

        {/* Main Content */}
        <section className="reconcilation__verification__body__main">
          {/* Document Preview Panel */}
          <div className="reconcilation__verification__image__view">
            {currentDocInfo.file_name && (
              <div className="document-view-header">
                <span title={currentDocInfo.file_name} style={{ flex: 1 }}>
                  {currentDocInfo.file_name.length > 25 
                    ? `${currentDocInfo.file_name.substring(0, 25)}...` 
                    : currentDocInfo.file_name}
                </span>
                {(() => {
                  if (!getExtension) return null;
                  const ext = getExtension.toLowerCase();
                  const isPdf = ext === 'pdf' && !!fileURL;
                  const isExcel = ['xls', 'xlsx', 'xlsb'].includes(ext);
                  const isCsv = ext === 'csv';
                  
                  if (isPdf || isExcel || isCsv) {
                    const tooltipText = `Download ${currentDocInfo.file_name || 'this file'}`;
                    
                    return (
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Tooltip title={tooltipText} arrow>
                          <button
                            onClick={handleDownloadCurrentDocument}
                            disabled={disabledState}
                            style={{
                              padding: '4px 10px',
                              background: 'rgba(255, 255, 255, 0.2)',
                              color: 'white',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              borderRadius: '6px',
                              cursor: disabledState ? 'not-allowed' : 'pointer',
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              transition: 'all 0.2s ease',
                              opacity: disabledState ? 0.5 : 1,
                            }}
                            onMouseEnter={(e) => {
                              if (!disabledState) {
                                e.target.style.background = 'rgba(255, 255, 255, 0.3)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = 'rgba(255, 255, 255, 0.2)';
                            }}
                          >
                            <span>📥</span>
                            <span>Download</span>
                          </button>
                        </Tooltip>
                      </Box>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
            <div className="document-view-content">
              {renderDocumentPreview()}
            </div>
          </div>

          {/* Data Table Panel */}
          <div className="reconcilation__verification__table">
            <div className="table-header-bar">
              <span className="table-header-title">
                Reconciliation Data
              </span>
              <span style={{ fontSize: '0.8rem', color: 'var(--reco-secondary)' }}>
                Document {selectedDocument} • Page {currentPage}
              </span>
            </div>
            <div className="reco-table-container">
              <ReconcilationTable 
                data={filteredData}
                setData={setReconcilationDetails}
                page_no={currentPage}
                disabledState={disabledState || readOnly}
                setDisabledState={setDisabledState}
                selectedDocument={selectedDocument}
                readOnly={readOnly}
              />
            </div>
          </div>
        </section>

        {/* Footer Actions - IMPORTANT: Submit Button - Always Visible */}
        <section 
          className="reconcilation__verification_button__main"
          style={{
            display: 'flex',
            visibility: 'visible',
            opacity: 1,
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            minHeight: '80px',
            maxHeight: '80px',
            width: '100%',
          }}
        >
          <div className="reconcilation__verification__button__area" style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            {readOnly ? (
              <div style={{ 
                padding: '14px 36px',
                color: '#666',
                fontSize: '0.95rem',
                fontStyle: 'italic'
              }}>
                Read-only mode (Admin View)
              </div>
            ) : (
              <button
                className="reco-submit-btn primary"
                onClick={handleSubmit}
                disabled={disabledState || !reconcilationDetails.length}
                style={{
                  display: 'flex',
                  visibility: 'visible',
                  opacity: (disabledState || !reconcilationDetails.length) ? 0.7 : 1,
                  minWidth: '220px',
                  fontSize: '1.1rem',
                  padding: '14px 36px',
                  fontWeight: '700',
                  cursor: (disabledState || !reconcilationDetails.length) ? 'not-allowed' : 'pointer',
                }}
              >
                <span style={{ fontSize: '1.2rem', marginRight: '8px' }}>✓</span>
                <span>Submit & Save</span>
              </button>
            )}
          </div>
        </section>
      </section>
    </section>
  );
}

export default ReconcilationVerification;
